import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Loader2, BookOpen, ChevronDown } from 'lucide-react';
import Icon from '../../../components/AppIcon';
import { bibleService } from '../../../services/bibleService';
import SignInPromptBanner from '../../../components/SignInPromptBanner';

const OT_BOOKS = [
  'Genesis','Exodus','Leviticus','Numbers','Deuteronomy','Joshua','Judges','Ruth',
  '1 Samuel','2 Samuel','1 Kings','2 Kings','1 Chronicles','2 Chronicles','Ezra',
  'Nehemiah','Esther','Job','Psalms','Proverbs','Ecclesiastes','Song of Solomon',
  'Isaiah','Jeremiah','Lamentations','Ezekiel','Daniel','Hosea','Joel','Amos',
  'Obadiah','Jonah','Micah','Nahum','Habakkuk','Zephaniah','Haggai','Zechariah','Malachi'
];

const NT_BOOKS = [
  'Matthew','Mark','Luke','John','Acts','Romans','1 Corinthians','2 Corinthians',
  'Galatians','Ephesians','Philippians','Colossians','1 Thessalonians','2 Thessalonians',
  '1 Timothy','2 Timothy','Titus','Philemon','Hebrews','James','1 Peter','2 Peter',
  '1 John','2 John','3 John','Jude','Revelation'
];

const ALL_BOOKS = [...OT_BOOKS, ...NT_BOOKS];

export default function BibleSearchDrawer({ isOpen, onClose, onVerseSelect, currentVersion }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [versions, setVersions] = useState([]);

  // Filters
  const [testament, setTestament] = useState('all'); // 'all' | 'ot' | 'nt'
  const [matchType, setMatchType] = useState('all'); // 'all' | 'exact'
  const [selectedVersion, setSelectedVersion] = useState('');
  const [specificBook, setSpecificBook] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [bookDropdownOpen, setBookDropdownOpen] = useState(false);

  const inputRef = useRef(null);
  const bookDropdownRef = useRef(null);

  // Load versions
  useEffect(() => {
    bibleService?.getBibleVersions()?.then(({ data }) => {
      if (data) setVersions(data);
    });
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef?.current?.focus(), 100);
    } else {
      setQuery('');
      setResults([]);
    }
  }, [isOpen]);

  // Close book dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (bookDropdownRef?.current && !bookDropdownRef?.current?.contains(e?.target)) {
        setBookDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Determine which books to show in the book picker
  const availableBooks = testament === 'ot' ? OT_BOOKS : testament === 'nt' ? NT_BOOKS : ALL_BOOKS;

  // Reset specific book if it's not in the new testament filter
  useEffect(() => {
    if (specificBook && !availableBooks?.includes(specificBook)) {
      setSpecificBook('');
    }
  }, [testament]);

  const doSearch = async (searchQuery) => {
    if (!searchQuery?.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await bibleService?.searchVerses(searchQuery, 50);
      if (error || !data) {
        setResults([]);
        return;
      }

      let filtered = data;

      // Testament filter
      if (testament === 'ot') {
        filtered = filtered?.filter(v => OT_BOOKS?.includes(v?.book_name));
      } else if (testament === 'nt') {
        filtered = filtered?.filter(v => NT_BOOKS?.includes(v?.book_name));
      }

      // Specific book filter
      if (specificBook) {
        filtered = filtered?.filter(v => v?.book_name === specificBook);
      }

      // Version filter
      if (selectedVersion) {
        filtered = filtered?.filter(v =>
          v?.version_short_name?.toLowerCase() === selectedVersion?.toLowerCase() ||
          v?.version_name?.toLowerCase()?.includes(selectedVersion?.toLowerCase())
        );
      }

      // Match type filter
      if (matchType === 'exact') {
        const lower = searchQuery?.toLowerCase();
        filtered = filtered?.filter(v => v?.text?.toLowerCase()?.includes(lower));
      }

      setResults(filtered);
    } catch (err) {
      console.error('Search error:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => doSearch(query), 350);
    return () => clearTimeout(timer);
  }, [query, testament, matchType, selectedVersion, specificBook]);

  const handleVerseClick = (verse) => {
    onVerseSelect?.(verse);
    onClose?.();
  };

  const highlightMatch = (text, term) => {
    if (!term?.trim()) return text;
    const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text?.split(regex);
    return parts?.map((part, i) =>
      regex?.test(part) ? <mark key={i} className="bg-amber-200 text-amber-900 rounded px-0.5">{part}</mark> : part
    );
  };

  const activeFilterCount = [
    testament !== 'all',
    matchType !== 'all',
    !!selectedVersion,
    !!specificBook,
  ]?.filter(Boolean)?.length;

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 animate-in fade-in duration-200"
          onClick={onClose}
        />
      )}
      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full z-50 flex flex-col shadow-2xl transition-transform duration-300 ease-in-out w-full sm:w-96`}
        style={{
          background: 'var(--color-background, #0f0f1a)',
          borderLeft: '1px solid var(--color-border, #2a2a3e)',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0"
          style={{ background: 'linear-gradient(to right, rgba(245,158,11,0.15), rgba(245,158,11,0.05), #1a1a2e)' }}>
          <div className="flex items-center gap-2">
            <Search size={18} className="text-amber-400" />
            <span className="font-semibold text-white text-sm">Bible Search</span>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-white/10 transition-colors text-gray-400 hover:text-white"
          >
            <X size={18} />
          </button>
        </div>

        {/* Search Input */}
        <div className="px-4 pt-3 pb-2 flex-shrink-0" style={{ background: '#1a1a2e' }}>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e?.target?.value)}
              placeholder="Search the Bible..."
              className="w-full pl-9 pr-4 py-2 rounded-md text-sm bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400"
            />
            {query && (
              <button
                onClick={() => { setQuery(''); setResults([]); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(p => !p)}
            className="mt-2 flex items-center gap-1.5 text-xs text-gray-400 hover:text-amber-400 transition-colors"
          >
            <Icon name="SlidersHorizontal" size={13} />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <span className="bg-amber-500 text-black text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
            <Icon name={showFilters ? 'ChevronUp' : 'ChevronDown'} size={13} />
          </button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="px-4 pb-3 flex-shrink-0 border-b border-border space-y-3" style={{ background: '#1a1a2e' }}>
            {/* Testament */}
            <div>
              <p className="text-xs text-gray-400 mb-1.5 font-medium uppercase tracking-wide">Testament</p>
              <div className="flex gap-2">
                {[
                  { value: 'all', label: 'All' },
                  { value: 'ot', label: 'OT Only' },
                  { value: 'nt', label: 'NT Only' },
                ]?.map(opt => (
                  <button
                    key={opt?.value}
                    onClick={() => setTestament(opt?.value)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      testament === opt?.value
                        ? 'bg-amber-500 text-black' :'bg-white/10 text-gray-300 hover:bg-white/20'
                    }`}
                  >
                    {opt?.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Match Type */}
            <div>
              <p className="text-xs text-gray-400 mb-1.5 font-medium uppercase tracking-wide">Match Type</p>
              <div className="flex gap-2">
                {[
                  { value: 'all', label: 'All Words' },
                  { value: 'exact', label: 'Exact Match' },
                ]?.map(opt => (
                  <button
                    key={opt?.value}
                    onClick={() => setMatchType(opt?.value)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      matchType === opt?.value
                        ? 'bg-amber-500 text-black' :'bg-white/10 text-gray-300 hover:bg-white/20'
                    }`}
                  >
                    {opt?.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Version */}
            <div>
              <p className="text-xs text-gray-400 mb-1.5 font-medium uppercase tracking-wide">Version</p>
              <select
                value={selectedVersion}
                onChange={e => setSelectedVersion(e?.target?.value)}
                className="w-full px-3 py-1.5 rounded-md text-xs bg-white/10 border border-white/20 text-white focus:outline-none focus:border-amber-400"
              >
                <option value="">All Versions</option>
                {versions?.map(v => (
                  <option key={v?.id || v?.short_name} value={v?.short_name}>
                    {v?.short_name} — {v?.version_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Specific Book */}
            <div ref={bookDropdownRef} className="relative">
              <p className="text-xs text-gray-400 mb-1.5 font-medium uppercase tracking-wide">Specific Book</p>
              <button
                onClick={() => setBookDropdownOpen(p => !p)}
                className="w-full flex items-center justify-between px-3 py-1.5 rounded-md text-xs bg-white/10 border border-white/20 text-white hover:border-amber-400 transition-colors"
              >
                <span>{specificBook || 'Any Book'}</span>
                <ChevronDown size={13} />
              </button>
              {bookDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 z-10 rounded-md border border-white/20 max-h-48 overflow-y-auto shadow-xl"
                  style={{ background: '#1a1a2e' }}>
                  <button
                    onClick={() => { setSpecificBook(''); setBookDropdownOpen(false); }}
                    className="w-full text-left px-3 py-2 text-xs text-gray-300 hover:bg-white/10 transition-colors"
                  >
                    Any Book
                  </button>
                  {availableBooks?.map(book => (
                    <button
                      key={book}
                      onClick={() => { setSpecificBook(book); setBookDropdownOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                        specificBook === book
                          ? 'bg-amber-500/20 text-amber-400' :'text-gray-300 hover:bg-white/10'
                      }`}
                    >
                      {book}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Clear Filters */}
            {activeFilterCount > 0 && (
              <button
                onClick={() => {
                  setTestament('all');
                  setMatchType('all');
                  setSelectedVersion('');
                  setSpecificBook('');
                }}
                className="text-xs text-amber-400 hover:text-amber-300 transition-colors flex items-center gap-1"
              >
                <X size={12} />
                Clear all filters
              </button>
            )}
          </div>
        )}

        {/* Results */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {loading && (
            <div className="flex items-center justify-center py-10">
              <Loader2 size={20} className="animate-spin text-amber-400" />
              <span className="ml-2 text-sm text-gray-400">Searching…</span>
            </div>
          )}

          {!loading && query && results?.length === 0 && (
            <div className="text-center py-10">
              <BookOpen size={36} className="mx-auto text-gray-600 mb-3" />
              <p className="text-sm text-gray-400">No results found</p>
              <p className="text-xs text-gray-500 mt-1">Try different keywords or adjust filters</p>
            </div>
          )}

          {!loading && !query && (
            <div className="text-center py-10">
              <Search size={36} className="mx-auto text-gray-600 mb-3" />
              <p className="text-sm text-gray-400">Type to search the Bible</p>
              <p className="text-xs text-gray-500 mt-1">Use filters to narrow results by testament, version, or book</p>
            </div>
          )}

          {!loading && results?.length > 0 && (
            <>
              <SignInPromptBanner message="Sign in to save verses to your library and add personal notes." />
              <p className="text-xs text-gray-500 mb-2">{results?.length} result{results?.length !== 1 ? 's' : ''}</p>
              {results?.map((verse, idx) => (
                <button
                  key={`${verse?.verse_id}-${idx}`}
                  onClick={() => handleVerseClick(verse)}
                  className="w-full text-left p-3 rounded-lg border border-white/10 hover:border-amber-400/50 hover:bg-white/5 transition-all group"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-amber-400 mb-1">
                        {verse?.reference || `${verse?.book_name} ${verse?.chapter_number}:${verse?.verse_number}`}
                      </p>
                      <p className="text-sm text-gray-300 leading-relaxed line-clamp-3">
                        {highlightMatch(verse?.text || '', query)}
                      </p>
                    </div>
                    <Icon name="ArrowRight" size={14} className="text-gray-600 group-hover:text-amber-400 transition-colors flex-shrink-0 mt-1" />
                  </div>
                </button>
              ))}
            </>
          )}
        </div>
      </div>
    </>
  );
}
